import { z } from "zod";
import { DemoCancellationCutoffMinutesSchema } from "./demo-scheduling.js";
import { PatientMessageChannelSchema, } from "./patient-messaging.js";
import { IanaTimeZoneSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Clinic message templates (PA-8 / EXECDEV-215): the wording of each kind of text and email, as
 * the clinic writes it with fill-in fields such as {clinic_name}. This module is the grammar,
 * the limits and the rules every renderer follows; packages/domain renders (server-side only:
 * agent-api's preview and the messaging lane), the store keeps a copy of the rules
 * (scripts/supabase parity tests), and the browser only parses for inline errors.
 *
 * - A template is a series per kind and channel (6 kinds x text/email = 12), each version
 *   immutable. A message records the version it was sent with (PatientMessageTemplateRef key
 *   PATIENT_MESSAGE_CLINIC_TEMPLATE_KEY), so editing changes future messages only.
 * - A text has a body only; an email a one-line subject and a plain-text body. No HTML.
 * - Fields are exactly `{name}` with a lower-case name from PATIENT_MESSAGE_MERGE_FIELDS. Any
 *   other brace, an unknown name or a name the kind does not take is refused; there is no
 *   escape for a literal brace. The reason for the visit is never a field.
 * - Every field the owner's ticket lists may be used (lead decision K2). The shipped defaults use
 *   only PATIENT_MESSAGE_MINIMAL_FIELDS, and PATIENT_MESSAGE_EXTENDED_FIELDS carry a caution: the
 *   clinic's electronic messaging notice must say messages hold them (counsel, LAUNCH-0).
 * - The renderer appends a fixed footer the template cannot change (patientMessageFooter), and
 *   both count toward the length limits.
 */
// ---------------------------------------------------------------------------
// Kinds and fields
// ---------------------------------------------------------------------------
/**
 * The template series a clinic writes. confirmation, reminder and intake_link are sent from
 * PA-8 on, cancellation and reschedule notices from PA-9 (lead decision L6), and document_ready
 * from PA-14 (PATIENT_MESSAGE_TEMPLATE_KIND_SENT). PA-7's manage_link message has no series: it
 * keeps the built-in default-v1 wording.
 */
export const PATIENT_MESSAGE_TEMPLATE_KINDS = [
    "confirmation",
    "reminder",
    "intake_link",
    "cancellation",
    "reschedule",
    "document_ready",
];
export const PatientMessageTemplateKindSchema = z.enum(PATIENT_MESSAGE_TEMPLATE_KINDS);
/** Whether anything sends a kind yet. The others show "Not sent yet" and can still be edited. */
export const PATIENT_MESSAGE_TEMPLATE_KIND_SENT = {
    confirmation: true,
    reminder: true,
    intake_link: true,
    cancellation: true,
    reschedule: true,
    document_ready: true,
};
/** The series an outbox message is written with, or null for a kind with no series. */
export function patientMessageTemplateKind(kind) {
    return kind === "manage_link" ? null : kind;
}
/**
 * The key a message sent with a clinic template records, with the series version (the message's
 * kind and channel name the series): "clinic-v3". PA-7's built-in wording stays "default-v1".
 */
export const PATIENT_MESSAGE_CLINIC_TEMPLATE_KEY = "clinic";
/** Every field the owner's ticket allows. No field holds the reason for the visit. */
export const PATIENT_MESSAGE_MERGE_FIELDS = [
    "clinic_name",
    "clinic_phone",
    "clinic_address",
    "appointment_date",
    "appointment_time",
    "appointment_type",
    "provider_name",
    "patient_first_name",
    "patient_last_name",
    "manage_link",
    "intake_link",
    "cancel_by",
    "document_link",
];
export const PatientMessageMergeFieldSchema = z.enum(PATIENT_MESSAGE_MERGE_FIELDS);
/** The fields the shipped default templates use: the clinic, the date and time, a link, the phone. */
export const PATIENT_MESSAGE_MINIMAL_FIELDS = [
    "clinic_name",
    "clinic_phone",
    "appointment_date",
    "appointment_time",
    "manage_link",
    "intake_link",
    "document_link",
];
/**
 * Fields the editor shows with a caution (lead decision K2): they put the patient's name, the
 * physician, the visit type or the clinic's address in a text or email, so the clinic's
 * electronic messaging notice must say so (the seeded notice promises only the clinic's name,
 * the time and a link). A visit type's name can be as revealing as a reason for the visit.
 */
export const PATIENT_MESSAGE_EXTENDED_FIELDS = [
    "patient_first_name",
    "patient_last_name",
    "provider_name",
    "appointment_type",
    "clinic_address",
];
/** The fields that are a link: a credential, never in an email subject, at most one per message. */
export const PATIENT_MESSAGE_LINK_FIELDS = [
    "manage_link",
    "intake_link",
    "document_link",
];
/**
 * Names refused with their own issue (forbidden_field) so the editor can say why: what the
 * patient told the clinic about the visit is never put in a message.
 */
export const PATIENT_MESSAGE_FORBIDDEN_FIELDS = [
    "reason",
    "reason_for_visit",
    "visit_reason",
    "chief_complaint",
    "complaint",
    "diagnosis",
    "symptoms",
    "notes",
];
const CLINIC_FIELDS = ["clinic_name", "clinic_phone", "clinic_address"];
const PATIENT_FIELDS = ["patient_first_name", "patient_last_name"];
const VISIT_FIELDS = [
    "appointment_date",
    "appointment_time",
    "appointment_type",
    "provider_name",
];
/**
 * The fields each kind takes. A booking's messages (confirmation, reminder, change of time)
 * take every field; a questionnaire link has no {manage_link} or {cancel_by}; a cancellation
 * has no link; a document notice is about no visit (PA-14 adds its link).
 */
export const PATIENT_MESSAGE_FIELDS_BY_KIND = {
    confirmation: [
        ...CLINIC_FIELDS,
        ...PATIENT_FIELDS,
        ...VISIT_FIELDS,
        "manage_link",
        "intake_link",
        "cancel_by",
    ],
    reminder: [
        ...CLINIC_FIELDS,
        ...PATIENT_FIELDS,
        ...VISIT_FIELDS,
        "manage_link",
        "intake_link",
        "cancel_by",
    ],
    reschedule: [
        ...CLINIC_FIELDS,
        ...PATIENT_FIELDS,
        ...VISIT_FIELDS,
        "manage_link",
        "intake_link",
        "cancel_by",
    ],
    intake_link: [...CLINIC_FIELDS, ...PATIENT_FIELDS, ...VISIT_FIELDS, "intake_link"],
    cancellation: [...CLINIC_FIELDS, ...PATIENT_FIELDS, ...VISIT_FIELDS],
    document_ready: [...CLINIC_FIELDS, ...PATIENT_FIELDS, "document_link"],
};
/**
 * How many links a kind's message carries. "one": exactly one link field ({manage_link} or
 * {intake_link}, once), because every message the outbox sends today mints one link of its own
 * (PA-7, owner decision O2); "none": no link field at all.
 */
export const PATIENT_MESSAGE_LINKS_BY_KIND = {
    confirmation: "one",
    reminder: "one",
    reschedule: "one",
    intake_link: "one",
    cancellation: "none",
    document_ready: "one",
};
/**
 * Fields a template must contain. Every text names the clinic first-hand ({clinic_name}), since
 * the sender is only a number; a questionnaire-link message carries {intake_link}.
 */
export function patientMessageRequiredFields(kind, channel) {
    const required = [];
    if (channel === "sms")
        required.push("clinic_name");
    if (kind === "intake_link")
        required.push("intake_link");
    return required;
}
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/** Lengths are UTF-16 code units, as the store counts them (PA-11's rule). */
export const PATIENT_MESSAGE_TEMPLATE_LIMITS = {
    /** A text template's body. */
    smsBodyMaxLength: 480,
    /** An email template's subject: one line. */
    emailSubjectMaxLength: 150,
    /** An email template's body. */
    emailBodyMaxLength: 5_000,
    /**
     * A rendered text, footer included, with every field at its longest
     * (PATIENT_MESSAGE_FIELD_MAX_LENGTH): at most 20 parts in either encoding (66 code units a
     * part when a pair would straddle), the outbox's limit, and under Twilio's 1,600.
     */
    smsRenderedMaxLength: 1_320,
    /** A rendered email subject, with every field at its longest. */
    emailSubjectRenderedMaxLength: 400,
    /** A rendered email body, footer included, with every field at its longest. */
    emailBodyRenderedMaxLength: 16_000,
    /** The preview warns when a text takes more parts than this (each part is billed). */
    smsPartsWarning: 2,
    /** Earlier versions of a series a read returns, newest first. */
    templateHistory: 20,
    /** What a preview or test draft may send before it is checked (larger is refused outright). */
    draftMaxLength: 10_000,
    /** Issues one check reports at most. */
    issues: 50,
};
/**
 * The most a field's value may take in a message. Longer clinic text is cut to it, an unusable
 * name falls back (cleanPatientMessageValue), and a longer link is refused.
 */
export const PATIENT_MESSAGE_FIELD_MAX_LENGTH = {
    clinic_name: 120,
    clinic_phone: 40,
    clinic_address: 160,
    appointment_date: 40,
    appointment_time: 20,
    appointment_type: 80,
    provider_name: 80,
    patient_first_name: 40,
    patient_last_name: 40,
    manage_link: 400,
    intake_link: 400,
    cancel_by: 64,
    document_link: 400,
};
export const PatientMessageTemplateIssueCodeSchema = z.enum([
    /** The part is empty (after line breaks are normalized and the ends trimmed). */
    "empty",
    /** Longer than the part's limit (limit). */
    "too_long",
    /** With every field at its longest, the message would pass its limit (limit). */
    "too_long_when_filled",
    /** A line break in an email subject. */
    "line_break",
    /** A tab or another control character (line feeds are allowed in a body). */
    "control_character",
    /** A broken character (a lone UTF-16 surrogate) that cannot be stored. */
    "unsaveable_character",
    /** Stored text starts or ends with a space or line break (reads only; writes trim). */
    "not_trimmed",
    /** A text has no subject. */
    "subject_not_allowed",
    /** An email needs a subject. */
    "subject_required",
    /** A brace that does not open or close a field (index). */
    "stray_brace",
    /** `{}` (index). */
    "empty_field",
    /** A name that is not a field (field, as typed, at most 40 characters; index). */
    "unknown_field",
    /** The reason for the visit, or another note the patient gave, which is never sent (index). */
    "forbidden_field",
    /** A field this kind does not take (field, index). */
    "field_not_allowed",
    /** A link in an email subject (field, index). */
    "link_in_subject",
    /** A field the kind or channel needs is missing (field). */
    "missing_field",
    /** This kind's message carries one link: add {manage_link} or {intake_link}. */
    "missing_link",
    /** More than one link (index of the second). */
    "too_many_links",
]);
export const PatientMessageTemplateIssueSchema = z
    .object({
    code: PatientMessageTemplateIssueCodeSchema,
    part: z.enum(["subject", "body"]),
    /** The field, or what was typed between the braces. */
    field: z.string().min(1).max(40).optional(),
    /** Where in the part (UTF-16 offset), for issues at one place. */
    index: z.number().int().min(0).max(PATIENT_MESSAGE_TEMPLATE_LIMITS.draftMaxLength).optional(),
    /** The limit passed, for the length issues. */
    limit: z.number().int().positive().optional(),
})
    .strict();
/** The longest name looked at between braces; a longer run is a stray brace. */
const MAX_FIELD_NAME = 40;
const FIELD_NAME = /^[a-z_]+$/;
const MERGE_FIELDS = new Set(PATIENT_MESSAGE_MERGE_FIELDS);
const FORBIDDEN_FIELDS = new Set(PATIENT_MESSAGE_FORBIDDEN_FIELDS);
const LINK_FIELDS = new Set(PATIENT_MESSAGE_LINK_FIELDS);
/**
 * Splits text into literal runs and `{field}` tokens. A token is a brace, 1-40 lower-case
 * letters or underscores of a known field, and a closing brace on the same line. Anything else
 * between braces is an issue and stays literal text.
 */
function tokenize(text) {
    const segments = [];
    const issues = [];
    let literal = "";
    const flush = () => {
        if (literal !== "")
            segments.push({ kind: "text", text: literal });
        literal = "";
    };
    let index = 0;
    while (index < text.length) {
        const character = text[index];
        if (character === "}") {
            issues.push({ code: "stray_brace", index });
            literal += character;
            index += 1;
            continue;
        }
        if (character !== "{") {
            literal += character;
            index += 1;
            continue;
        }
        const close = text.indexOf("}", index + 1);
        const inner = close === -1 ? "" : text.slice(index + 1, close);
        if (close === -1 || inner.length > MAX_FIELD_NAME || /[{\n]/.test(inner)) {
            issues.push({ code: "stray_brace", index });
            literal += character;
            index += 1;
            continue;
        }
        const token = text.slice(index, close + 1);
        if (inner === "") {
            issues.push({ code: "empty_field", index });
            literal += token;
        }
        else if (FIELD_NAME.test(inner) && MERGE_FIELDS.has(inner)) {
            flush();
            segments.push({ kind: "field", field: inner, index });
        }
        else {
            const normalized = inner
                .trim()
                .toLowerCase()
                .replace(/[\s-]+/g, "_");
            issues.push({
                code: FORBIDDEN_FIELDS.has(normalized) ? "forbidden_field" : "unknown_field",
                field: inner,
                index,
            });
            literal += token;
        }
        index = close + 1;
    }
    flush();
    return { segments, issues };
}
/**
 * A template part as literal text and fields, in order: what a renderer substitutes. Throws on
 * any brace issue, so a renderer never sends a template the editor would refuse.
 */
export function patientMessageTemplateSegments(text) {
    const { segments, issues } = tokenize(text);
    if (issues.length > 0)
        throw new Error("PATIENT_MESSAGE_TEMPLATE_INVALID");
    return [...segments];
}
/** The fields a template uses, once each, in PATIENT_MESSAGE_MERGE_FIELDS order. */
export function patientMessageTemplateFields(template) {
    const used = new Set();
    for (const text of [template.subject ?? "", template.body]) {
        for (const segment of tokenize(text).segments) {
            if (segment.kind === "field")
                used.add(segment.field);
        }
    }
    return PATIENT_MESSAGE_MERGE_FIELDS.filter((field) => used.has(field));
}
/** The extended fields a template uses: what its editor and preview caution about. */
export function patientMessageTemplateCautions(template) {
    const used = new Set(patientMessageTemplateFields(template));
    return PATIENT_MESSAGE_EXTENDED_FIELDS.filter((field) => used.has(field));
}
/** The link field a template's body uses, or null. */
export function patientMessageTemplateLinkField(template) {
    for (const segment of tokenize(template.body).segments) {
        if (segment.kind === "field" && LINK_FIELDS.has(segment.field)) {
            return segment.field;
        }
    }
    return null;
}
/**
 * Which page a template's one link opens (a message mints exactly that link when it is sent):
 *
 * - {manage_link}: the appointment's manage page.
 * - {intake_link} in an intake_link message: the questionnaire.
 * - {intake_link} in a confirmation, reminder or change-of-time notice: the questionnaire while
 *   the patient still has it to answer before the visit (intake status required or sent, a new
 *   patient: PA-7 lead decision J1), and the manage page otherwise, so the link is never a dead
 *   one. The shipped defaults use it, so new patients get their questionnaire.
 *
 * Null when the template has no link (a cancellation, a document notice).
 */
export function patientMessageTemplateLinkPurpose(kind, linkField, intakeStatus) {
    if (linkField === null)
        return null;
    if (linkField === "manage_link")
        return "manage";
    if (linkField === "document_link")
        return "document";
    if (kind === "intake_link")
        return "intake";
    return intakeStatus === "required" || intakeStatus === "sent" ? "intake" : "manage";
}
// ---------------------------------------------------------------------------
// Text rules
// ---------------------------------------------------------------------------
/** Unicode control characters (Cc), as PA-11's stored-text rule refuses them. */
const CONTROL_CHARACTER = /\p{Cc}/u;
/** A UTF-16 surrogate standing alone, which PostgreSQL text cannot hold. */
const LONE_SURROGATE = /\p{Cs}/u;
/**
 * Line breaks as line feeds and the ends trimmed: what a publish stores (normalizeConsentDocument
 * Text's rule). Previews and test drafts are checked after the same normalization.
 */
export function normalizePatientMessageTemplateText(value) {
    return value.replace(/\r\n?/g, "\n").trim();
}
/** The template part limits for a channel. */
function partLimit(channel, part) {
    if (part === "subject")
        return PATIENT_MESSAGE_TEMPLATE_LIMITS.emailSubjectMaxLength;
    return channel === "sms"
        ? PATIENT_MESSAGE_TEMPLATE_LIMITS.smsBodyMaxLength
        : PATIENT_MESSAGE_TEMPLATE_LIMITS.emailBodyMaxLength;
}
function renderedLimit(channel, part) {
    if (part === "subject")
        return PATIENT_MESSAGE_TEMPLATE_LIMITS.emailSubjectRenderedMaxLength;
    return channel === "sms"
        ? PATIENT_MESSAGE_TEMPLATE_LIMITS.smsRenderedMaxLength
        : PATIENT_MESSAGE_TEMPLATE_LIMITS.emailBodyRenderedMaxLength;
}
/**
 * The longest a part can render: its literal text, each field at its longest and, for a body,
 * the longest footer the channel can get. A template is refused when this passes the rendered
 * limit, so a published template never makes a message too long to send.
 */
export function patientMessageTemplateMaxRenderedLength(channel, part, text) {
    let total = 0;
    for (const segment of tokenize(text).segments) {
        total +=
            segment.kind === "text"
                ? segment.text.length
                : PATIENT_MESSAGE_FIELD_MAX_LENGTH[segment.field];
    }
    return part === "body" ? total + patientMessageFooterMaxLength(channel) : total;
}
function textIssues(text, channel, part, options) {
    const issues = [];
    if (text.trim() === "") {
        issues.push({ code: "empty", part });
        return issues;
    }
    const limit = partLimit(channel, part);
    if (text.length > limit)
        issues.push({ code: "too_long", part, limit });
    if (options.stored === true && text !== text.trim())
        issues.push({ code: "not_trimmed", part });
    if (LONE_SURROGATE.test(text))
        issues.push({ code: "unsaveable_character", part });
    // A body keeps line feeds; a subject is one line. Any other control character (a tab, a CR
    // left after normalization) is refused.
    if (part === "subject" && /[\n\r]/.test(text))
        issues.push({ code: "line_break", part });
    const lines = part === "subject" ? text.replace(/[\n\r]/g, "") : text.replaceAll("\n", "");
    if (CONTROL_CHARACTER.test(lines))
        issues.push({ code: "control_character", part });
    if (options.filledLength === true && text.length <= limit) {
        const rendered = renderedLimit(channel, part);
        if (patientMessageTemplateMaxRenderedLength(channel, part, text) > rendered) {
            issues.push({ code: "too_long_when_filled", part, limit: rendered });
        }
    }
    return issues;
}
/**
 * Everything wrong with a template for a kind and channel, in the order the editor lists them
 * (subject first), at most PATIENT_MESSAGE_TEMPLATE_LIMITS.issues. Empty means it may be
 * published, previewed and sent. The store applies the same rules (filled length included).
 */
export function patientMessageTemplateIssues(template, options = {}) {
    const { kind, channel } = template;
    const issues = [];
    const allowed = new Set(PATIENT_MESSAGE_FIELDS_BY_KIND[kind]);
    const used = new Set();
    const links = [];
    const parts = [];
    if (channel === "sms") {
        if (template.subject !== null)
            issues.push({ code: "subject_not_allowed", part: "subject" });
    }
    else if (template.subject === null) {
        issues.push({ code: "subject_required", part: "subject" });
    }
    else {
        parts.push(["subject", template.subject]);
    }
    parts.push(["body", template.body]);
    for (const [part, text] of parts) {
        issues.push(...textIssues(text, channel, part, options));
        const tokenized = tokenize(text);
        issues.push(...tokenized.issues.map((issue) => ({ ...issue, part })));
        for (const segment of tokenized.segments) {
            if (segment.kind !== "field")
                continue;
            if (!allowed.has(segment.field)) {
                issues.push({
                    code: "field_not_allowed",
                    part,
                    field: segment.field,
                    index: segment.index,
                });
                continue;
            }
            used.add(segment.field);
            if (LINK_FIELDS.has(segment.field)) {
                if (part === "subject") {
                    issues.push({
                        code: "link_in_subject",
                        part,
                        field: segment.field,
                        index: segment.index,
                    });
                }
                else {
                    links.push({ part, index: segment.index });
                }
            }
        }
    }
    const required = patientMessageRequiredFields(kind, channel);
    for (const field of required) {
        if (!used.has(field))
            issues.push({ code: "missing_field", part: "body", field });
    }
    // A kind that names its link (intake_link) already said which one is missing.
    const linkNamed = required.some((field) => LINK_FIELDS.has(field));
    if (PATIENT_MESSAGE_LINKS_BY_KIND[kind] === "one" && links.length === 0 && !linkNamed) {
        issues.push({
            code: "missing_link",
            part: "body",
            ...(kind === "document_ready" ? { field: "document_link" } : {}),
        });
    }
    const extraLink = links[1];
    if (extraLink !== undefined) {
        issues.push({ code: "too_many_links", part: extraLink.part, index: extraLink.index });
    }
    return issues.slice(0, PATIENT_MESSAGE_TEMPLATE_LIMITS.issues);
}
/** Plain staff copy for an issue (the editor may word its own). */
export function patientMessageTemplateIssueMessage(issue) {
    const where = issue.part === "subject" ? "subject" : "message";
    const field = issue.field === undefined ? "" : `{${issue.field}}`;
    switch (issue.code) {
        case "empty":
            return issue.part === "subject" ? "Enter a subject" : "Enter the message";
        case "too_long":
            return `Keep the ${where} to ${issue.limit ?? 0} characters or fewer`;
        case "too_long_when_filled":
            return `Filled in with the longest details, the ${where} could pass ${issue.limit ?? 0} characters. Shorten it or use fewer fields`;
        case "line_break":
            return "Keep the subject on one line";
        case "control_character":
            return `The ${where} cannot contain tabs or control characters`;
        case "unsaveable_character":
            return `The ${where} contains a character that cannot be saved`;
        case "not_trimmed":
            return `The ${where} cannot start or end with a space or line break`;
        case "subject_not_allowed":
            return "A text has no subject";
        case "subject_required":
            return "Enter a subject";
        case "stray_brace":
            return "Braces are only for fields such as {clinic_name}";
        case "empty_field":
            return "Put a field name between the braces, such as {clinic_name}";
        case "unknown_field":
            return `${field} is not a field. Use one from the list`;
        case "forbidden_field":
            return "The reason for the visit and other notes from the patient are never sent";
        case "field_not_allowed":
            return `${field} can't be used in this message`;
        case "link_in_subject":
            return "Links can't go in the subject";
        case "missing_field":
            return issue.field === "clinic_name"
                ? "A text must name the clinic: add {clinic_name}"
                : `Add ${field}`;
        case "missing_link":
            return issue.field === "document_link"
                ? "Add the document link: {document_link}"
                : "Add a link: {manage_link} or {intake_link}";
        case "too_many_links":
            return "Use one link per message";
    }
}
/**
 * Adds every template issue to a zod refinement, at the part's path under `path` (each issue's
 * params.templateIssue holds the structured issue).
 */
export function refinePatientMessageTemplate(value, context, options, path = []) {
    for (const issue of patientMessageTemplateIssues(value, options)) {
        context.addIssue({
            code: "custom",
            message: patientMessageTemplateIssueMessage(issue),
            path: [...path, issue.part],
            params: { templateIssue: issue },
        });
    }
}
/** A template's text: a subject (emails only; null for a text) and a body. */
export const PatientMessageTemplateTextSchema = z
    .object({
    subject: z.string().max(PATIENT_MESSAGE_TEMPLATE_LIMITS.draftMaxLength).nullable(),
    body: z.string().max(PATIENT_MESSAGE_TEMPLATE_LIMITS.draftMaxLength),
})
    .strict();
/**
 * A template as a renderer takes it: valid for its kind and channel (the stored rules; the
 * renderer itself caps what it renders). The domain renderer parses its input with this.
 */
export const PatientMessageTemplateSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    ...PatientMessageTemplateTextSchema.shape,
})
    .strict()
    .superRefine((value, context) => refinePatientMessageTemplate(value, context, { stored: true }));
/**
 * Text as a clinic sends it to be published, previewed or tested: line breaks normalized and the
 * ends trimmed, then every rule, the filled length included.
 */
export const PatientMessageTemplateDraftSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    subject: z
        .string()
        .max(PATIENT_MESSAGE_TEMPLATE_LIMITS.draftMaxLength)
        .transform(normalizePatientMessageTemplateText)
        .nullable(),
    body: z
        .string()
        .max(PATIENT_MESSAGE_TEMPLATE_LIMITS.draftMaxLength)
        .transform(normalizePatientMessageTemplateText),
})
    .strict()
    .superRefine((value, context) => refinePatientMessageTemplate(value, context, { filledLength: true }));
/** Checks a draft for a kind and channel the way a publish would: normalized, every rule. */
export function patientMessageTemplateDraftIssues(template) {
    return patientMessageTemplateIssues({
        ...template,
        subject: template.subject === null ? null : normalizePatientMessageTemplateText(template.subject),
        body: normalizePatientMessageTemplateText(template.body),
    }, { filledLength: true });
}
// ---------------------------------------------------------------------------
// SMS parts (GSM-7 and UCS-2)
// ---------------------------------------------------------------------------
/** GSM 03.38 basic character set (one septet each). ESC is left out: it only prefixes extensions. */
const GSM_BASIC = new Set(Array.from("@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà"));
/** GSM 03.38 extension table: an escape plus the character, two septets each. */
const GSM_EXTENSION = new Set(Array.from("\f^{}\\[~]|€"));
export const SmsSegmentsSchema = z
    .object({
    encoding: z.enum(["GSM-7", "UCS-2"]),
    units: z.number().int().min(0),
    segments: z.number().int().min(0),
})
    .strict();
/**
 * Pack characters (each with its unit cost) into parts. A two-unit character (a GSM extension or
 * a UTF-16 surrogate pair) never straddles two parts, as carriers send them.
 */
function packSegments(costs, single, multi) {
    const units = costs.reduce((total, cost) => total + cost, 0);
    if (units === 0)
        return 0;
    if (units <= single)
        return 1;
    let segments = 1;
    let used = 0;
    for (const cost of costs) {
        if (used + cost > multi) {
            segments += 1;
            used = 0;
        }
        used += cost;
    }
    return segments;
}
/** How many SMS parts a text takes, and in which encoding (the editor's counter and the lane). */
export function smsSegments(text) {
    const characters = Array.from(text);
    const gsm = characters.every((character) => GSM_BASIC.has(character) || GSM_EXTENSION.has(character));
    if (gsm) {
        const costs = characters.map((character) => (GSM_EXTENSION.has(character) ? 2 : 1));
        return {
            encoding: "GSM-7",
            units: costs.reduce((total, cost) => total + cost, 0),
            segments: packSegments(costs, 160, 153),
        };
    }
    const costs = characters.map((character) => character.length);
    return {
        encoding: "UCS-2",
        units: text.length,
        segments: packSegments(costs, 70, 67),
    };
}
// ---------------------------------------------------------------------------
// What a renderer is given, and the values it fills in
// ---------------------------------------------------------------------------
/**
 * What the store gives a renderer for one message (the lane's claim or authorize, and agent-api's
 * preview with sample details): only the keys the template and footer need
 * (patientMessageContextKeys), never the reason for the visit, notes or anything clinical. null
 * means "none": the field's fallback renders (PATIENT_MESSAGE_FIELD_FALLBACKS). A key the
 * template needs that is absent fails the message closed, never a guessed value.
 *
 * - patientFirstName / patientLastName: from the linked chart (preferred or legal first name,
 *   legal last name) when there is one, otherwise the booking's name split at the first space
 *   (lead decision K2). The renderer keeps a name only when it is letters, spaces, hyphens and
 *   apostrophes, 40 characters or fewer (patientMessageName).
 * - providerName: null for a clinic-assigned booking or a placeholder physician.
 * - clinicAddress: null while the clinic's address is the placeholder.
 * - Links are not here: the lane mints the message's one link when it sends
 *   (patientMessageTemplateLinkPurpose).
 */
export const PatientMessageMergeContextSchema = z
    .object({
    clinicName: z.string().trim().min(1).max(240),
    clinicPhone: z.string().max(240).nullable().optional(),
    clinicAddress: z.string().max(240).nullable().optional(),
    timeZone: IanaTimeZoneSchema.optional(),
    startsAt: IsoDateTimeSchema.optional(),
    cancellationCutoffMinutes: DemoCancellationCutoffMinutesSchema.optional(),
    appointmentTypeName: z.string().max(240).nullable().optional(),
    providerName: z.string().max(240).nullable().optional(),
    patientFirstName: z.string().max(240).nullable().optional(),
    patientLastName: z.string().max(240).nullable().optional(),
})
    .strict();
/**
 * The context keys each field is filled from. {cancel_by} is the start minus the clinic's
 * cancellation cutoff, on the clinic's clock (the start itself when the cutoff is 0).
 */
export const PATIENT_MESSAGE_FIELD_CONTEXT = {
    clinic_name: ["clinicName"],
    clinic_phone: ["clinicPhone"],
    clinic_address: ["clinicAddress"],
    appointment_date: ["startsAt", "timeZone"],
    appointment_time: ["startsAt", "timeZone"],
    appointment_type: ["appointmentTypeName"],
    provider_name: ["providerName"],
    patient_first_name: ["patientFirstName"],
    patient_last_name: ["patientLastName"],
    manage_link: [],
    intake_link: [],
    document_link: [],
    cancel_by: ["startsAt", "timeZone", "cancellationCutoffMinutes"],
};
const CONTEXT_KEY_ORDER = Object.keys(PatientMessageMergeContextSchema.shape);
/**
 * The context keys a message needs: clinicName always, those of each field it uses, and for an
 * email the clinic's phone and address for its footer. The store returns exactly these.
 */
export function patientMessageContextKeys(channel, fields) {
    const keys = new Set(["clinicName"]);
    for (const field of fields) {
        for (const key of PATIENT_MESSAGE_FIELD_CONTEXT[field])
            keys.add(key);
    }
    if (channel === "email") {
        keys.add("clinicPhone");
        keys.add("clinicAddress");
    }
    return CONTEXT_KEY_ORDER.filter((key) => keys.has(key));
}
/**
 * Seeded placeholders that are not real values (PA-3/PA-4 settings): never rendered literally,
 * they count as "none" and the field's fallback renders instead.
 */
export const PATIENT_MESSAGE_PLACEHOLDER_VALUES = new Set([
    "Contact the clinic for its address",
    "Unassigned clinician",
    "Assigned clinician",
]);
/**
 * What a field renders when its value is none (or unusable). Fields without an entry are always
 * given (the clinic's name, the date and time, the link); a missing one fails the message closed.
 * An empty fallback also drops one space beside it, so "Hi {patient_first_name}," reads "Hi,".
 */
export const PATIENT_MESSAGE_FIELD_FALLBACKS = {
    clinic_phone: "the clinic",
    clinic_address: "the clinic",
    appointment_type: "appointment",
    provider_name: "your clinic physician",
    patient_first_name: "",
    patient_last_name: "",
};
const NAME = /^[\p{L}\p{M}]+(?:[ '’-][\p{L}\p{M}]+)*$/u;
/**
 * A name as a message may carry it (lead decision K2): letters with single spaces, hyphens or
 * apostrophes between them, at most PATIENT_MESSAGE_FIELD_MAX_LENGTH.patient_first_name (40)
 * UTF-16 code units after spaces are collapsed. Anything else (digits, dots, a URL, a long run of
 * text typed as a name online) gives null, so the field's fallback renders and a booking form can
 * never make the clinic's sender carry arbitrary text.
 */
export function patientMessageName(value) {
    if (value === null || value === undefined)
        return null;
    const name = value.normalize("NFC").replace(/\s+/gu, " ").trim();
    return name !== "" &&
        name.length <= PATIENT_MESSAGE_FIELD_MAX_LENGTH.patient_first_name &&
        NAME.test(name)
        ? name
        : null;
}
/** Cuts text to at most max UTF-16 code units without splitting a surrogate pair. */
function cut(text, max) {
    if (text.length <= max)
        return text;
    return text
        .slice(0, max)
        .replace(/\p{Cs}$/u, "")
        .trimEnd();
}
/**
 * One context value as a message may carry it, or null for none: on one line (control, format
 * and line-separator characters dropped, spaces collapsed), a seeded placeholder treated as none,
 * a name kept only as patientMessageName allows, and other text cut to the field's maximum.
 * Links and the date and time are not cleaned here: the renderer makes them.
 */
export function cleanPatientMessageValue(field, value) {
    if (value === null || value === undefined)
        return null;
    if (field === "patient_first_name" || field === "patient_last_name") {
        return patientMessageName(value);
    }
    const text = value
        .normalize("NFC")
        .replace(/\p{Cf}/gu, "")
        .replace(/[\p{Cc}\p{Zl}\p{Zp}]/gu, " ")
        .replace(/\s+/gu, " ")
        .trim();
    if (text === "" || PATIENT_MESSAGE_PLACEHOLDER_VALUES.has(text))
        return null;
    return cut(text, PATIENT_MESSAGE_FIELD_MAX_LENGTH[field]);
}
// ---------------------------------------------------------------------------
// Footers
// ---------------------------------------------------------------------------
/**
 * Every text ends with this, after a line break (Twilio Advanced Opt-Out answers STOP). There is
 * no "replies are not read" line: a reply gets the one auto-reply instead (owner decision O3).
 */
export const PATIENT_MESSAGE_SMS_FOOTER = "Reply STOP to stop texts.";
/** What joins a body and its footer. */
export const PATIENT_MESSAGE_FOOTER_SEPARATOR = {
    sms: "\n",
    email: "\n\n",
};
const EMAIL_FOOTER_CLOSING = [
    "Replies to this email are not read. For emergencies, call 911.",
    "To stop these emails, tell the clinic.",
];
/**
 * The fixed footer a template cannot change, without its separator. A text: the STOP line. An
 * email (plain text): a "--" line, the clinic's name, its address and phone when it has them,
 * that replies are not read (911 for emergencies), and how to stop emails.
 */
export function patientMessageFooter(channel, clinic) {
    if (channel === "sms")
        return PATIENT_MESSAGE_SMS_FOOTER;
    const name = cleanPatientMessageValue("clinic_name", clinic.clinicName);
    if (name === null)
        throw new Error("PATIENT_MESSAGE_CLINIC_MISSING");
    const address = cleanPatientMessageValue("clinic_address", clinic.clinicAddress);
    const phone = cleanPatientMessageValue("clinic_phone", clinic.clinicPhone);
    return [
        "--",
        name,
        ...(address === null ? [] : [address]),
        ...(phone === null ? [] : [`Phone: ${phone}`]),
        ...EMAIL_FOOTER_CLOSING,
    ].join("\n");
}
/** The longest footer (with its separator) a channel can get. */
export function patientMessageFooterMaxLength(channel) {
    const footer = patientMessageFooter(channel, {
        clinicName: "x".repeat(PATIENT_MESSAGE_FIELD_MAX_LENGTH.clinic_name),
        clinicAddress: "x".repeat(PATIENT_MESSAGE_FIELD_MAX_LENGTH.clinic_address),
        clinicPhone: "x".repeat(PATIENT_MESSAGE_FIELD_MAX_LENGTH.clinic_phone),
    });
    return PATIENT_MESSAGE_FOOTER_SEPARATOR[channel].length + footer.length;
}
// ---------------------------------------------------------------------------
// The shipped defaults
// ---------------------------------------------------------------------------
const DETAILS_LINK_EMAIL = "To see your appointment, and answer any questions the clinic has before your visit, open this link:";
/**
 * Version 1 of every series at every clinic, source default. Minimal fields only
 * (PATIENT_MESSAGE_MINIMAL_FIELDS): the clinic, the date and time, one link and the clinic phone;
 * never a name, the visit type or the reason. The store seeds the same text (the SQL parity test
 * compares them), and "Restore default text" publishes it again. Starting points for counsel
 * (LAUNCH-0), not approved wording.
 */
export const PATIENT_MESSAGE_DEFAULT_TEMPLATES = {
    confirmation: {
        sms: {
            subject: null,
            body: "{clinic_name}: Your appointment is booked for {appointment_date} at {appointment_time}.\nDetails: {intake_link}\nQuestions? Call {clinic_phone}.",
        },
        email: {
            subject: "Your appointment at {clinic_name}",
            body: `Your appointment at {clinic_name} is booked for {appointment_date} at {appointment_time}.\n\n${DETAILS_LINK_EMAIL}\n{intake_link}\n\nQuestions? Call {clinic_phone}.`,
        },
    },
    reminder: {
        sms: {
            subject: null,
            body: "{clinic_name}: Reminder of your appointment on {appointment_date} at {appointment_time}.\nDetails: {intake_link}\nQuestions? Call {clinic_phone}.",
        },
        email: {
            subject: "Reminder: your appointment at {clinic_name}",
            body: `This is a reminder of your appointment at {clinic_name} on {appointment_date} at {appointment_time}.\n\n${DETAILS_LINK_EMAIL}\n{intake_link}\n\nQuestions? Call {clinic_phone}.`,
        },
    },
    intake_link: {
        sms: {
            subject: null,
            body: "{clinic_name}: Please answer a few questions before your appointment on {appointment_date} at {appointment_time}:\n{intake_link}",
        },
        email: {
            subject: "Questions before your visit to {clinic_name}",
            body: "{clinic_name} has a few questions for your appointment on {appointment_date} at {appointment_time}.\n\nPlease answer them before you come. Open this link:\n{intake_link}\n\nQuestions? Call {clinic_phone}.",
        },
    },
    cancellation: {
        sms: {
            subject: null,
            body: "{clinic_name}: Your appointment on {appointment_date} at {appointment_time} is cancelled.\nTo book again or for questions, call {clinic_phone}.",
        },
        email: {
            subject: "Your appointment at {clinic_name} is cancelled",
            body: "Your appointment at {clinic_name} on {appointment_date} at {appointment_time} is cancelled.\n\nTo book again or for questions, call {clinic_phone}.",
        },
    },
    reschedule: {
        sms: {
            subject: null,
            body: "{clinic_name}: Your appointment is now on {appointment_date} at {appointment_time}.\nDetails: {intake_link}\nQuestions? Call {clinic_phone}.",
        },
        email: {
            subject: "New time for your appointment at {clinic_name}",
            body: `Your appointment at {clinic_name} is now on {appointment_date} at {appointment_time}.\n\n${DETAILS_LINK_EMAIL}\n{intake_link}\n\nQuestions? Call {clinic_phone}.`,
        },
    },
    document_ready: {
        sms: {
            subject: null,
            body: "{clinic_name}: A document from the clinic is ready for you. Open it with your date of birth:\n{document_link}\nQuestions? Call {clinic_phone}.",
        },
        email: {
            subject: "A document from {clinic_name}",
            body: "{clinic_name} has a document ready for you.\n\nTo download it, open this link and enter your date of birth:\n{document_link}\n\nQuestions? Call {clinic_phone}.",
        },
    },
};
//# sourceMappingURL=patient-message-templates.js.map